/**
 * safeGuardRules preset — voice input for the SafeGuard "Review Rules" block.
 *
 * The SafeGuard reviewer AI needs four free-text policies to rate outgoing
 * emails: tone/DOs, DON'Ts, target-audience context, and hard no-goes.
 * Typing four rich textareas is friction; talking through six guiding
 * questions is much faster (and usually produces MORE detail than typing).
 *
 * Schema type: 'fields'
 *   Extracts four long_string fields that map 1:1 onto the state keys used
 *   by SafeGuardSettings.js:
 *     - emailDos       → "Email DOs" (tone, style, structure)
 *     - emailDonts     → "Email DON'Ts" (words / phrases / behaviours)
 *     - targetAudience → who the emails go to
 *     - veryImportant  → non-negotiable rules
 *
 * The AI is instructed to MERGE with any `context.existingValues` instead of
 * blindly overwriting — so users editing existing rules with voice can add
 * detail without losing what they had.
 *
 * Guided prompts (6 questions) live in `intro.questions` and are rendered
 * as a numbered checklist by the SpeakEngineModal. Users can loosely follow
 * them while talking — order doesn't matter, they can skip anything, and
 * everything they don't cover just stays blank in the extracted output.
 */

import React, { useEffect, useState } from 'react';

// The four rule buckets, in the order they appear in SafeGuardSettings.
// Keeping labels here (not just field ids) means the preview form is
// self-describing without touching the calling component.
const RULE_FIELDS_META = [
  { id: 'emailDos',       label: 'How your emails should sound',           placeholder: 'Tone, structure, length, call-to-action…' },
  { id: 'emailDonts',     label: 'What must never appear',                 placeholder: 'Words, phrases, patterns to always avoid…' },
  { id: 'targetAudience', label: 'Who you are writing to',                 placeholder: 'Roles, seniorities, industry, mindset…' },
  { id: 'veryImportant',  label: 'Absolute rules (never break these)',     placeholder: 'Legal, ethical, brand-safety non-negotiables…' },
];

/**
 * Compact editable form for the extracted rules. All four textareas are
 * shown together so the user can see everything the AI produced at once
 * and tweak wording before saving. Row count is smaller than the main
 * settings screen because this is a review, not the primary composition
 * surface.
 */
function SafeGuardRulesPreview({ structured, onConfirm, onCancel, saving }) {
  const initial = structured?.fields || {};
  const [values, setValues] = useState({
    emailDos: initial.emailDos || '',
    emailDonts: initial.emailDonts || '',
    targetAudience: initial.targetAudience || '',
    veryImportant: initial.veryImportant || '',
  });

  // Re-seed if the modal is reopened with fresh AI output while mounted.
  useEffect(() => {
    if (initial) {
      setValues({
        emailDos: initial.emailDos || '',
        emailDonts: initial.emailDonts || '',
        targetAudience: initial.targetAudience || '',
        veryImportant: initial.veryImportant || '',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structured?.fields?.emailDos]);

  const update = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const filledCount = Object.values(values).filter((v) => (v || '').trim()).length;

  return (
    <div className="speakEngine__body speakEngine__body--preview">
      <div className="speakEngine__previewHead">
        <div>
          <h3>{filledCount}/4 rule sets extracted</h3>
          {structured?.summary && <p className="speakEngine__previewSummary">{structured.summary}</p>}
        </div>
      </div>

      <div className="speakEngine__fieldsGrid">
        {RULE_FIELDS_META.map((field) => (
          <label key={field.id} className="speakEngine__field speakEngine__field--wide">
            <span className="speakEngine__fieldLabel">
              {field.label}
              <span className="speakEngine__fieldCounter">{(values[field.id] || '').length} chars</span>
            </span>
            <textarea
              className="speakEngine__fieldTextarea"
              rows={5}
              value={values[field.id]}
              onChange={(e) => update(field.id, e.target.value)}
              placeholder={field.placeholder}
            />
          </label>
        ))}
      </div>

      <div className="speakEngine__previewActions">
        <button type="button" className="speakEngine__btn speakEngine__btn--ghost" onClick={onCancel} disabled={saving}>
          Back to transcript
        </button>
        <button
          type="button"
          className="speakEngine__btn speakEngine__btn--primary"
          onClick={() => onConfirm(values)}
          disabled={saving || filledCount === 0}
        >
          {saving
            ? <><span className="speakEngine__btnSpinner" aria-hidden /> Applying…</>
            : <>Apply {filledCount === 4 ? 'all rules' : `${filledCount} rule ${filledCount === 1 ? 'set' : 'sets'}`}</>}
        </button>
      </div>
    </div>
  );
}

const safeGuardRulesPreset = {
  id: 'safeGuardRules',
  title: 'Teach SafeGuard your voice',
  subtitle: 'SafeGuard turns your answers into review rules.',
  intro: {
    heading: 'Tap and talk it through',
    hint: 'You don\'t have to answer everything or in order. Cover what feels relevant and the AI extracts what it hears. 2–3 minutes is plenty.',
    // These are what the modal renders as a numbered "guided prompts" list.
    // Deliberately conversational, not corporate — first-person questions
    // the user can just riff on.
    questionsHeading: 'Guiding questions',
    questions: [
      'Who are you writing to? (role, company size, industry, how they think and talk)',
      'What tone should your emails have? Give me 2–3 words (e.g. direct + warm + zero fluff).',
      'What must NEVER appear? Words, phrases, sales-y patterns, cringe openings, over-promises…',
      'How long should emails be, and what does the ideal call-to-action look like?',
      'Are there hard no-goes? Legal, ethical, brand-safety things the AI is never allowed to break.',
      'Anything special about your audience the reviewer should know? Sensitivities, insider context, pain points…',
    ],
  },
  organizeCta: 'Turn into rules',
  organizingText: 'AI is grouping your answers into the four rule buckets SafeGuard uses to review every outgoing email. Usually 3–8 seconds.',
  doneText: () => 'SafeGuard rules updated.',
  schema: {
    type: 'fields',
    fields: [
      {
        id: 'emailDos',
        label: 'Email DOs (tone, style, structure)',
        type: 'long_string',
        hint: 'Concrete guidance about how emails should sound and be built. Write as bullet-style short lines starting with "- ". Cover tone, structure, ideal length, opener style, CTA style. Include ONLY what the speaker actually covered — do not invent guidance.',
      },
      {
        id: 'emailDonts',
        label: 'Email DON\'Ts (words / phrases / behaviours)',
        type: 'long_string',
        hint: 'Explicit list of words, phrases, sales-clichés, or behaviours the reviewer must flag. Write as bullet-style short lines starting with "- ". Be concrete and quotable ("no ALL CAPS", "no \'URGENT\'"), not abstract.',
      },
      {
        id: 'targetAudience',
        label: 'Target audience',
        type: 'long_string',
        hint: 'Who receives these emails — role, seniority, industry, mindset, language they use, decision drivers. Write as bullet-style short lines starting with "- ". This is context, not a manifesto — keep it factual.',
      },
      {
        id: 'veryImportant',
        label: 'Non-negotiable rules',
        type: 'long_string',
        hint: 'Hard rules that must NEVER be broken — legal (GDPR, compliance), ethical, brand-safety, promise-limits ("never quote specific ROI numbers"). Write as bullet-style short lines starting with "- ". Only include things the speaker explicitly flagged as absolute.',
      },
    ],
  },
  PreviewComponent: SafeGuardRulesPreview,
};

export default safeGuardRulesPreset;
